import { Button, FileInput, Spinner } from 'flowbite-react';

import { useState } from 'react';
import { Img } from 'react-image';
import { useMutation, useQueryClient } from 'react-query';
import { updateUserById, getPolicyForS3NonVideo } from 'src/apis/user';
import { useToast } from 'src/context/ToastProvider';
import { useUserContext } from 'src/context/UserProvider';
import { checkResumeContent } from 'src/apis/parseResume';
import { handleS3Upload } from 'src/utils/imageUpload';
import { get } from 'lodash';
import React from 'react';
interface Props {
	customCta?: React.ReactElement;
	onFinish?: () => void;
}

const ProfileUploadResume = ({ customCta, onFinish }: Props) => {
	const toast = useToast();
	const queryClient = useQueryClient();
	const { user } = useUserContext();
	const [isResumeUploadedLoading, setIsResumeUploadedLoading] = useState<boolean>(false);

	const updateProfileByIdFn = useMutation(['updateUserById'], updateUserById, {
		onSuccess() {
			toast({
				title: 'Action was successfully completed',
				type: 'success',
			});
			queryClient.refetchQueries(['verifyCookie']);
			queryClient.refetchQueries(['getUserProfile']);
			queryClient.refetchQueries(['getAllApplications']);
			// fetch user profile
			setIsResumeUploadedLoading(false);
			onFinish && onFinish();
		},
		onError(error: Error) {
			const errorMessage = get(error, ['response', 'data', 'message'], error.message);
			toast({
				type: 'error',
				title: errorMessage,
			});

			setIsResumeUploadedLoading(false);
		},
	});

	const handleUploadResume = async (resume: File) => {
		const fileSizeMB = resume.size / (1024 * 1024); // Convert bytes to MB
		if (fileSizeMB > 5) {
			toast({
				type: 'error',
				title: 'File size exceeds the maximum limit of 5 MB',
				duration: 10000,
			});
			return;
		}
		setIsResumeUploadedLoading(true);
		let imageUrl = '';
		if (resume && user) {
			// fileName, fileLocation, mime
			const policy = await getPolicyForS3NonVideo(resume.name, 'resumeUrls', resume.type, true);
			const uploadedUrl = await handleS3Upload(policy, resume);
			if (uploadedUrl) imageUrl = uploadedUrl;
			const { error } = await checkResumeContent(imageUrl, String(user._id));
			if (error) {
				toast({
					title: 'Resume is not valid',
					type: 'error',
				});
				setIsResumeUploadedLoading(false);
				return;
			}
			if (imageUrl) {
				handleUploadResumeLinkToDatabase(imageUrl);
			}
		} else {
			setIsResumeUploadedLoading(false);
		}
	};

	const handleUploadResumeLinkToDatabase = (resumeLink: string) => {
		updateProfileByIdFn.mutate({
			id: String(user?._id),
			data: {
				profile: {
					resumeLink: resumeLink,
				},
			},
		});
	};

	return (
		<>
			<div className="relative flex items-center gap-2">
				{customCta ? (
					customCta &&
					React.cloneElement(customCta, {
						disabled: isResumeUploadedLoading,
						onClick: () => {
							const fileInput = document.getElementById('hukumu-profile-resume-file-input');
							fileInput?.click();
						},
					})
				) : (
					<Button
						disabled={isResumeUploadedLoading}
						color={'dark'}
						onClick={() => {
							// setStep(1);
							const fileInput = document.getElementById('hukumu-profile-resume-file-input');
							fileInput?.click();
						}}
						className="text-white !text-sm !font-medium font-inter bg-[#18222D] border border-[#7777E7] hover:!bg-[#1f2a37]"
					>
						<Img
							className="mr-2"
							loading="lazy"
							src="/assets/cloudUploadIcon.svg"
							alt="cloudUploadIcon"
							width="20"
							height="20"
						/>
						Upload resume
					</Button>
				)}

				{isResumeUploadedLoading ? <Spinner className="absolute bottom-0 left-0 right-0 top-0 m-auto" /> : null}
			</div>

			<div className="relative mt-[8px] hidden">
				<FileInput
					disabled={isResumeUploadedLoading}
					id="hukumu-profile-resume-file-input"
					accept=".pdf,doc,docx"
					helperText="DOC, DOCX, PDF — supported formats"
					className="w-full text-xs font-normal italic"
					onChange={event => {
						if (event.target.files) {
							handleUploadResume(event.target.files[0]);
						}
					}}
				/>
				{isResumeUploadedLoading ? <Spinner className="absolute bottom-0 left-0 right-0 top-0 m-auto" /> : null}
			</div>
		</>
	);
};

export default ProfileUploadResume;
